/**
 * API клиент для Telegram Client (MTProto)
 * Дата: 30.12.2025
 * Назначение: Работа с Telegram Client API (QR-авторизация, статус, поиск, отключение)
 */
import { apiClient } from './client';

export interface QRAuthResult {
  qr_code_url: string;
  qr_code_data: string;
  expires_at: string;
}

export interface AuthStatus {
  status: 'connected' | 'disconnected' | 'auth_required' | '2fa_required';
  phone?: string;
  password_hint?: string;
  messages_count?: number; // Количество синхронизированных сообщений
}

// Дата: 26.01.2026 - TODO-035: Итерация 3 - типы для edge cases
export interface PhoneAuthError {
  type: '2fa_required' | 'flood_wait' | 'code_expired' | 'invalid_code' | 'session_exists' | 'signup_required';
  message: string;
  wait_seconds?: number; // Для flood_wait
  password_hint?: string; // Для 2FA
}

export interface VerifyCodeResponse {
  success: boolean;
  user_id?: number;
  phone?: string;
  requires_2fa?: boolean;
  password_hint?: string;
}

export interface UserInfo {
  phone: string;
  found: boolean;
  user_id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  has_photo?: boolean;
  reason?: string;
}

/**
 * Запустить QR-авторизацию
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function startQRAuth(tenantId?: string): Promise<QRAuthResult> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const response = await apiClient.post<QRAuthResult>('/telegram-client/qr-auth', {}, { headers });
  return response.data;
}

/**
 * Проверить статус авторизации
 * @param tenantId - ID тенанта (для SuperAdmin)
 * Дата: 10.01.2026 - Исправлено: tenant_id как query param
 */
export async function checkAuthStatus(tenantId?: string): Promise<AuthStatus> {
  const params: Record<string, string> = {};
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.get<AuthStatus>('/telegram-client/status', { params });
  return response.data;
}

/**
 * Поиск пользователя по номеру телефона
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function searchByPhone(phone: string, tenantId?: string): Promise<UserInfo> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const response = await apiClient.get<UserInfo>('/telegram-client/search', {
    params: { phone },
    headers,
  });
  return response.data;
}

/**
 * Поиск пользователя по Telegram ID (числовой user_id) или username
 * Дата: 05.02.2026 - Добавлено для TODO-051 (инициирование диалога по TG ID)
 * Дата: 12.02.2026 - Тест 2.2: бэкенд теперь принимает и username (если не число → resolveUsername)
 * @param tgId - Telegram user_id (числовой) или username (без @)
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function searchByTgId(tgId: string, tenantId?: string): Promise<UserInfo> {
  const params: Record<string, string> = { tg_id: tgId };
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.get<UserInfo>('/telegram-client/user', { params });
  return response.data;
}

/**
 * Поиск пользователя по Telegram username
 * Дата: 12.02.2026 - Тест 2.2: отдельный метод для поиска по username
 * Бэкенд сам резолвит username через contacts.resolveUsername
 * @param username - Telegram username (с @ или без)
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function searchByUsername(username: string, tenantId?: string): Promise<UserInfo> {
  // Убираем @ если есть
  const cleanUsername = username.startsWith('@') ? username.slice(1) : username;
  const params: Record<string, string> = { tg_id: cleanUsername };
  if (tenantId) params.tenant_id = tenantId;
  const response = await apiClient.get<UserInfo>('/telegram-client/user', { params });
  return response.data;
}

/**
 * Отключить Telegram Client
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function disconnect(tenantId?: string): Promise<void> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  await apiClient.post('/telegram-client/disconnect', {}, { headers });
}

/**
 * Отправить код авторизации на телефон
 * Дата: 26.01.2026 - TODO-035: Добавлено для phone auth
 * @param phone - Номер телефона (например, "+79261234567")
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function sendCode(phone: string, tenantId?: string): Promise<{ code_hash: string; phone: string }> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  const response = await apiClient.post<{ code_hash: string; phone: string }>(
    '/telegram-client/send-code',
    { phone },
    { headers }
  );
  return response.data;
}

/**
 * Верифицировать код авторизации
 * Дата: 26.01.2026 - TODO-035: Добавлено для phone auth
 * @param phone - Номер телефона
 * @param code - Код из SMS/Telegram
 * @param codeHash - Хеш кода из sendCode
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function verifyCode(
  phone: string,
  code: string,
  codeHash: string,
  tenantId?: string
): Promise<VerifyCodeResponse> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  try {
    const response = await apiClient.post<VerifyCodeResponse>(
      '/telegram-client/verify-code',
      { phone, code, code_hash: codeHash },
      { headers }
    );
    return response.data;
  } catch (error: any) {
    // Дата: 26.01.2026 - TODO-035: Итерация 3 - обработка PhoneAuthError
    if (error.response?.data?.type) {
      throw error.response.data as PhoneAuthError;
    }
    throw error;
  }
}

/**
 * Верифицировать пароль 2FA
 * Дата: 26.01.2026 - TODO-035: Итерация 3
 * @param phone - Номер телефона
 * @param password - Пароль 2FA
 * @param tenantId - ID тенанта (для SuperAdmin)
 */
export async function verifyPassword(
  phone: string,
  password: string,
  tenantId?: string
): Promise<{ success: boolean; user_id?: number; phone?: string }> {
  const headers = tenantId ? { 'X-Tenant-ID': tenantId } : {};
  try {
    const response = await apiClient.post<{ success: boolean; user_id?: number; phone?: string }>(
      '/telegram-client/verify-password',
      { phone, password },
      { headers }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.type) {
      throw error.response.data as PhoneAuthError;
    }
    throw error;
  }
}
